import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { easeInOut, rem } from 'polished';

interface Props {
    label: string;
    id: string;
    handleClick: (id: string) => void;
    active: boolean;
    noMargin?: boolean;
    disabled?: boolean;
    asToggle?: boolean;
}

export const Chip: FC<Props> = ({ label, id, handleClick, active, noMargin, disabled, asToggle }) => {
    return (
        <StyledChip
            onClick={() => handleClick(id)}
            active={active}
            noMargin={noMargin}
            disabled={disabled}
            aria-pressed={asToggle ? active : undefined}
        >
            {active && <Dot aria-hidden="true" />}
            {label}
        </StyledChip>
    );
};

const Dot = styled.span`
    display: inline-block;
    width: ${rem(6)};
    height: ${rem(6)};
    border-radius: 50%;
    background: currentColor;
    flex-shrink: 0;
`;

const StyledChip = styled.button<{ active: boolean; noMargin?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: ${rem(6)};
    padding: ${rem(6)} ${rem(14)};
    margin: ${(p) => (p.noMargin ? '0' : `${rem(10)} ${rem(8)} 0 0`)};
    border-radius: ${rem(20)};
    font-size: ${rem(14)};
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    transition:
        background-color 200ms ${easeInOut('quad')},
        color 200ms ${easeInOut('quad')},
        border-color 200ms ${easeInOut('quad')},
        box-shadow 200ms ${easeInOut('quad')};

    ${(p) =>
        p.active
            ? css`
                  background-color: var(--color-primary);
                  color: #fff;
                  border: 2px solid var(--color-primary);
                  box-shadow: 0 ${rem(2)} ${rem(8)} rgba(54, 53, 93, 0.25);
              `
            : css`
                  background-color: transparent;
                  color: var(--color-primary);
                  border: 2px solid rgba(54, 53, 93, 0.3);
              `}

    &:hover:not(:disabled) {
        background-color: var(--color-primary);
        color: #fff;
        border-color: var(--color-primary);
        box-shadow: 0 ${rem(2)} ${rem(8)} rgba(54, 53, 93, 0.2);
    }

    &:focus-visible {
        outline: 2px solid var(--color-secondary);
        outline-offset: 3px;
    }

    &:disabled {
        opacity: 0.4;
        cursor: default;
        box-shadow: none;
    }
`;
